import { Loader } from 'gocclr-shared-ui/dist/elements/loader/loader';
import { useEffect, useRef, useState } from 'react';
import { Route, createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import { getApplicationBasePath, initBookingContext } from './app-context/accounts-context';
import { lazyWithPreload } from './helpers/lazy-preload';
import { SafeSuspense } from './components/suspense/safe-suspense';
import { PAGES } from './model/constants';
import { GoCclStyledThemeProvider, setTheme } from 'gocclr-shared-ui/dist/theming';
import { useGlobalAdvisories } from './hooks/use-advisories';
import { NotFound } from './pages/errors/404';
import { Unauthorized } from './pages/errors/403';
import { BaseLayout } from './layouts/base-layout';
import { LoginSubLayout } from './layouts/login-sub-layout';
import { HelmetProvider } from 'react-helmet-async';
import { LoginSafeSuspense } from './components/suspense/login-safe-suspense';

import './accounts-app.scss';

export interface AccountsAppProps {
}

const Home = lazyWithPreload(() => import(/* webpackChunkName: 'home-host' */'./pages/home/home'));
const Login = lazyWithPreload(() => import(/* webpackChunkName: 'login-host' */'./pages/login/login'));
const PostLogin = lazyWithPreload(() => import(/* webpackChunkName: 'post-login-host' */'./pages/login/post-login'));
const ForgotUserName = lazyWithPreload(() => import(/* webpackChunkName: 'forgot-username-host' */'./pages/login/forgot-username/forgot-username'));
const ForgotPassword = lazyWithPreload(() => import(/* webpackChunkName: 'forgot-password-host' */'./pages/login/forgot-password/forgot-password'));
const ResetPassword = lazyWithPreload(() => import(/* webpackChunkName: 'reset-password-host' */'./pages/login/reset-password/reset-password'));
const MyProfile = lazyWithPreload(() => import(/* webpackChunkName: 'my-profile-host' */'./pages/profile/view/view-profile'));
const EditProfile = lazyWithPreload(() => import(/* webpackChunkName: 'edit-profile-host' */'./pages/profile/edit/edit-profile'));
const AgencyCommissions = lazyWithPreload(() => import(/* webpackChunkName: 'agency-commissions-host' */'./pages/agency/commissions/agency-commissions'));
const AddAgentProfile = lazyWithPreload(() => import(/* webpackChunkName: 'add-agent-profile-host' */'./pages/manage-agents/add/add-agent-profile'));
const EditAgentProfile = lazyWithPreload(() => import(/* webpackChunkName: 'edit-agent-profile-host' */'./pages/manage-agents/edit/edit-agent-profile'));
const ManageAgents = lazyWithPreload(() => import(/* webpackChunkName: 'manage-agents-host' */'./pages/manage-agents/manage-agents'));
const PendingRequests = lazyWithPreload(() => import(/* webpackChunkName: 'pending-requests-host' */'./pages/pending-requests/pending-requests'));
const AgencyView = lazyWithPreload(() => import(/* webpackChunkName: 'agency-view-host' */'./pages/agency/view/agency-view'));
const ProductFiles = lazyWithPreload(() => import(/* webpackChunkName: 'product-files-host' */'./pages/product-files/product-files'));
const ViewTaFinder = lazyWithPreload(() => import(/* webpackChunkName: 'view-ta-finder-host' */'./pages/ta-finder/view/view-ta-finder'));
const EditTaFinder = lazyWithPreload(() => import(/* webpackChunkName: 'edit-ta-finder-host' */'./pages/ta-finder/edit/edit-ta-finder'));

function AccountsApp() {
    const [loaded, setLoaded] = useState<boolean>(false);
    const advisoriesContainerRef = useRef<HTMLDivElement>(null);
    const advisoriesApi = useGlobalAdvisories(advisoriesContainerRef);

    useEffect(() => {
        initBookingContext(true).then(() => {
            setTheme('goccl-2025');
            setLoaded(true);
        });
    }, []);

    if (!loaded) {
        return <Loader fixed={true} />;
    }

    const basePath = getApplicationBasePath();

    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route element={<BaseLayout advisoriesApi={advisoriesApi} advidoriesContainerRef={advisoriesContainerRef} />} >
                <Route path={PAGES.HOME} element={<SafeSuspense><Home advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={PAGES.HOME_WITH_LANG} element={<SafeSuspense><Home advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.LOGIN}`} element={<SafeSuspense><LoginSubLayout /></SafeSuspense>}>
                    <Route index element={<SafeSuspense><Login advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                    <Route path={`${PAGES.FORGOT_USERNAME}`} element={<SafeSuspense><ForgotUserName advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                    <Route path={`${PAGES.FORGOT_PASSWORD}`} element={<SafeSuspense><ForgotPassword advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                    <Route path={`${PAGES.RESET_PASSWORD}`} element={<SafeSuspense><ResetPassword advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                </Route>
                <Route path={`${PAGES.MY_PROFILE}`} element={<SafeSuspense><MyProfile advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.EDIT_PROFILE}`} element={<SafeSuspense><EditProfile advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.AGENCY_COMMISSIONS}`} element={<SafeSuspense><AgencyCommissions advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.AGENCY_VIEW}`} element={<SafeSuspense><AgencyView advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.ADD_AGENT_PROFILE}`} element={<SafeSuspense><AddAgentProfile advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.EDIT_AGENT_PROFILE}`} element={<SafeSuspense><EditAgentProfile advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.MANAGE_AGENTS}`} element={<SafeSuspense><ManageAgents advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.PENDING_REQUESTS}`} element={<SafeSuspense><PendingRequests advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.PRODUCT_FILES}`} element={<SafeSuspense><ProductFiles advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.VIEW_TRAVEL_AGENT_FINDER}`} element={<SafeSuspense><ViewTaFinder advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={`${PAGES.EDIT_TRAVEL_AGENT_FINDER}`} element={<SafeSuspense><EditTaFinder advisoriesApi={advisoriesApi} /></SafeSuspense>} />
                <Route path={PAGES.POST_LOGIN} element={<LoginSafeSuspense><PostLogin advisoriesApi={advisoriesApi} /></LoginSafeSuspense>} />
                <Route path={PAGES.ACCESS_DENIED} element={<Unauthorized />} />
                <Route path="*" element={<NotFound />} />
            </Route>
        ),
        {
            basename: basePath
        }
    );

    return (
        <HelmetProvider>
            <GoCclStyledThemeProvider>
                <RouterProvider router={router} useTransitions={false}/>
            </GoCclStyledThemeProvider>
        </HelmetProvider>
    );
}

export default AccountsApp;